import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Typography, Box, Alert, Chip, Stack, Switch, FormControlLabel, Divider,
} from "@mui/material";
import {
  PRC_PRIMARY_GRADIENT, PRC_PRIMARY_GRADIENT_HOVER, PRC_PRIMARY_SHADOW,
} from "./_pricingTheme";

/**
 * Edit dialog for one row of syt_pricing_weight_tiers.
 *
 * Editable: tier_name, min_weight_kg, max_weight_kg, multiplier_min,
 *           multiplier_max, multiplier_point, is_active, description.
 *
 * Read-only: tier_code (used as identifier), is_full_truck (structural flag).
 *
 * Client-side guard ensures multiplier_min ≤ multiplier_point ≤ multiplier_max
 * before the PUT. The server re-validates and returns HTTP 400 with
 * `errorCode: "POINT_OUT_OF_RANGE"` if bypassed.
 */
export default function WeightTierEditDialog({ item, onClose, onSubmit }) {
  const [draft, setDraft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (item) {
      setDraft({
        tier_name: item.tier_name ?? "",
        min_weight_kg: item.min_weight_kg ?? "",
        max_weight_kg: item.max_weight_kg ?? "",
        multiplier_min: item.multiplier_min ?? "",
        multiplier_max: item.multiplier_max ?? "",
        multiplier_point: item.multiplier_point ?? "",
        is_active: item.is_active ?? true,
        description: item.description ?? "",
      });
      setLocalError(null);
    }
  }, [item]);

  if (!item || !draft) return null;

  const isFullTruck = !!item.is_full_truck;

  const validate = () => {
    if (!String(draft.tier_name).trim()) return "Tier name cannot be empty";
    const mn = parseFloat(draft.multiplier_min);
    const mx = parseFloat(draft.multiplier_max);
    const pt = parseFloat(draft.multiplier_point);
    if ([mn, mx, pt].some((n) => Number.isNaN(n))) {
      return "multiplier_min / max / point must all be numeric";
    }
    if (mn > mx) return "multiplier_min cannot exceed multiplier_max";
    if (pt < mn || pt > mx) {
      return `multiplier_point must lie within [${mn}, ${mx}]`;
    }
    if (!isFullTruck) {
      const lo = draft.min_weight_kg === "" ? null : parseFloat(draft.min_weight_kg);
      const hi = draft.max_weight_kg === "" ? null : parseFloat(draft.max_weight_kg);
      if (lo != null && Number.isNaN(lo)) return "min_weight_kg must be numeric or blank";
      if (hi != null && Number.isNaN(hi)) return "max_weight_kg must be numeric or blank";
      if (lo != null && hi != null && lo >= hi) return "min_weight_kg must be less than max_weight_kg";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setLocalError(err); return; }
    setLocalError(null);
    setSubmitting(true);

    const patch = {
      tier_name: String(draft.tier_name).trim(),
      multiplier_min: parseFloat(draft.multiplier_min),
      multiplier_max: parseFloat(draft.multiplier_max),
      multiplier_point: parseFloat(draft.multiplier_point),
      is_active: !!draft.is_active,
      description: draft.description == null ? null : String(draft.description),
    };
    if (!isFullTruck) {
      patch.min_weight_kg = draft.min_weight_kg === "" ? null : parseFloat(draft.min_weight_kg);
      patch.max_weight_kg = draft.max_weight_kg === "" ? null : parseFloat(draft.max_weight_kg);
    }

    const result = await onSubmit(item.id, patch);
    if (!result?.ok) {
      setLocalError(result?.message || "Save failed");
    }
    setSubmitting(false);
  };

  const numField = (key, label, opts = {}) => (
    <TextField
      fullWidth
      label={label}
      type="number"
      value={draft[key]}
      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      inputProps={{ step: opts.step ?? 0.01, ...(opts.inputProps || {}) }}
      InputProps={opts.InputProps}
      helperText={opts.helper}
      disabled={opts.disabled}
      size="small"
    />
  );

  const update = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          borderLeft: "5px solid",
          borderImage: "linear-gradient(180deg, #007C84, #016268) 1",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #ECFDF5, #ECFEFF)",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#0F172A" }}>
            {item.tier_name}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#007C84", fontWeight: 700 }}>
            {item.tier_code}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={isFullTruck ? "type · full_truck" : "type · partial"}
            sx={{
              fontWeight: 700,
              bgcolor: isFullTruck ? "#FEF3C7" : "#DCFCE7",
              color: isFullTruck ? "#92400E" : "#065F46",
            }}
          />
          {!draft.is_active && (
            <Chip
              size="small"
              label="inactive"
              variant="outlined"
              sx={{ fontWeight: 700, color: "#DC2626", borderColor: "#FCA5A5" }}
            />
          )}
        </Stack>

        <TextField
          fullWidth size="small"
          label="Tier name (display)"
          value={draft.tier_name}
          onChange={update("tier_name")}
          sx={{ mb: 2 }}
        />

        {!isFullTruck && (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {numField("min_weight_kg", "Min weight (kg)", {
              step: 0.5,
              helper: "Inclusive lower bound. Blank = open.",
            })}
            {numField("max_weight_kg", "Max weight (kg)", {
              step: 0.5,
              helper: "Exclusive upper bound. Blank = open.",
            })}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontWeight: 800, color: "#0F766E", fontSize: 13, mb: 1 }}>
          Multiplier configuration
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          {numField("multiplier_min", "Min", { step: 0.1, disabled: isFullTruck })}
          {numField("multiplier_max", "Max", { step: 0.1, disabled: isFullTruck })}
          {numField("multiplier_point", "Active point", {
            step: 0.1,
            disabled: isFullTruck,
            helper: isFullTruck ? "Full-truck rows ignore the multiplier." : "Must lie within [min, max].",
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={!!draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              color="success"
            />
          }
          label={
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
              Active (engine reads this row)
            </Typography>
          }
        />

        <TextField
          fullWidth size="small"
          label="Description"
          value={draft.description ?? ""}
          onChange={update("description")}
          multiline minRows={2}
          sx={{ mt: 2 }}
        />

        {localError && (
          <Alert severity="warning" sx={{ mt: 2 }}>{localError}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            background: PRC_PRIMARY_GRADIENT,
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            boxShadow: PRC_PRIMARY_SHADOW,
            "&:hover": { background: PRC_PRIMARY_GRADIENT_HOVER },
            "&.Mui-disabled": { background: "#E5E7EB", color: "#94A3B8" },
          }}
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
